import { observer } from 'mobx-react-lite';
import {
    temperature as temperature_1,
    weatherItemTodayAriaLabelText,
    weatherItemForecastAriaLabelText,
} from './WeatherSurfaceItem.locstring.json';
import loc, { format } from 'owa-localize';
import * as React from 'react';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';

import WeatherCard from './WeatherCard';
import WeatherEnabledStatus from 'owa-service/lib/contract/WeatherEnabledStatus';
import weatherStore from '../store';

import { Callout, DirectionalHint } from '@fluentui/react/lib/Callout';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { FocusTrapZone } from '@fluentui/react/lib/FocusTrapZone';

import { getGuid } from 'owa-guid';
import { isToday, observableToday } from 'owa-observable-datetime';
import { OwaDate, differenceInCalendarDays, formatMonthDay } from 'owa-datetime';
import { PerformanceDatapoint } from 'owa-analytics';
import { trace } from 'owa-trace';
import { updateCurrentLocationIndex } from '../actions/updateCurrentLocationIndex';
import { isFeatureEnabled } from 'owa-feature-flags';

const iconSvgPrefix = 'SkyCode_';
const unknownWeatherIconPath = require('../resources/SkyCode_44.svg');
let styles = require('./WeatherSurfaceItem.scss');
let classNames = require('classnames/bind').bind(styles);

/**
 * Props for Weather surface item
 */
export interface WeatherSurfaceItemProps {
    date: OwaDate;
    showInTopRight?: boolean;
    shouldShowWeekDayTemp?: () => boolean;
}

/**
 * Component for rendering weather information for each day, when available
 */
export default observer(function WeatherSurfaceItem(props: WeatherSurfaceItemProps) {
    const focusTrapZone = React.useRef<FocusTrapZone>();
    const [shouldShowCallout, setShouldShowCallout] = React.useState<boolean>(false);
    let shouldShow = props.shouldShowWeekDayTemp?.() ?? true;
    const renderCurrentWeather = (date: OwaDate, showInTopRight: boolean): JSX.Element => {
        let defaultLocation = weatherStore.weatherLocations[0];
        if (!defaultLocation.Conditions) {
            trace.warn(
                'First location has no weather conditions information, ignoring current weather render.'
            );
            return null;
        }

        let temperature = shouldShow ? defaultLocation.Conditions.Temperature : null;

        let iconClass = iconSvgPrefix + defaultLocation.Conditions.SkyCode;
        let label = format(
            loc(weatherItemTodayAriaLabelText),
            formatMonthDay(observableToday()),
            defaultLocation.Conditions.SkyText,
            defaultLocation.Conditions.Temperature
        );
        return renderWeather(date, showInTopRight, iconClass, label, temperature);
    };
    const renderWeatherForecast = (date: OwaDate, showInTopRight: boolean): JSX.Element => {
        let defaultLocation = weatherStore.weatherLocations[0];
        let daysFromToday = differenceInCalendarDays(date, observableToday());
        if (defaultLocation.MultidayForecast?.DailyForecasts) {
            let daysWithWeather = defaultLocation.MultidayForecast.DailyForecasts.length;
            let showWeather = daysFromToday >= 0 && daysFromToday < daysWithWeather;
            if (showWeather) {
                let dailyForcasts = defaultLocation.MultidayForecast.DailyForecasts;
                let currentForecast = dailyForcasts[daysFromToday];
                if (!currentForecast) {
                    //Ignoring weather forecast rendering for location index ${daysFromToday} since there is no daily forecast for this day.`
                    return null;
                }
                let iconClass = iconSvgPrefix + currentForecast.SkyCode;
                let label = format(
                    loc(weatherItemForecastAriaLabelText),
                    formatMonthDay(date),
                    currentForecast.SkyText,
                    currentForecast.Low,
                    currentForecast.High
                );
                return renderWeather(date, showInTopRight, iconClass, label, null);
            } else {
                //`Ignoring weather forecast rendering for location index ${daysFromToday} since there are forecast for only ${daysWithWeather} days.`
                return null;
            }
        } else {
            let locationId = defaultLocation.Id ? defaultLocation.Id.EntityId : '<null>';
            trace.warn(
                `Ignoring weather forecast rendering for location ${locationId} because there is no available forecast data.`
            );
            return null;
        }
    };
    const renderWeather = (
        date: OwaDate,
        showInTopRight: boolean,
        iconClass: string,
        label: string,
        temperature: number | null
    ) => {
        // Add a guid to the id so that every weather button has distinct Id, required in cases like splitview which will have multiple instances
        let id = iconClass + getGuid();
        let iconFile = require('../resources/' + iconClass + '.svg');
        let icon = iconFile ? iconFile : unknownWeatherIconPath; //set to unknown weather icon if can't find svg file in ../resources
        return (
            <DefaultButton
                id={id}
                ariaLabel={label}
                className={classNames(styles.weatherContainer, { rightTopCorner: showInTopRight })}
                onClick={showCallout}
                iconProps={{
                    className: styles.skyCodeImage,
                    imageProps: {
                        shouldFadeIn: false,
                        src: icon,
                        styles: {
                            root: styles.skyCodeImageRoot,
                        },
                        // image is intentionally larger than its container in order for image to be the right visual size
                        width: 32,
                        height: 32,
                    },
                }}>
                {temperature !== null && (
                    <span className={styles.temperatureSpan} aria-hidden="true">
                        {format(loc(temperature_1), temperature)}
                    </span>
                )}
                {shouldShowCallout && renderCallout(date, id)}
            </DefaultButton>
        );
    };
    const renderCallout = (date: OwaDate, id: string): JSX.Element => {
        var weatherAnchor = '#' + id;
        let locationIndex = weatherStore.currentLocationIndex;
        let numLocations = weatherStore.weatherLocations.length;
        if (locationIndex < 0 || locationIndex >= numLocations) {
            trace.warn(
                `Ignoring weather card render. Requested location index ${locationIndex} is out of bounds.`
            );
            return null;
        }
        let currentLocation = weatherStore.weatherLocations[locationIndex];
        if (
            !(
                currentLocation.ClickThrough &&
                currentLocation.Conditions &&
                currentLocation.Conditions.Attribution &&
                currentLocation.Id &&
                currentLocation.MultidayForecast &&
                currentLocation.MultidayForecast.DailyForecasts
            )
        ) {
            trace.warn(
                `Ignoring weather card render. Requested location index ${locationIndex} has null data.`
            );
            return null;
        }
        return (
            <Callout
                className={styles.weatherCard}
                isBeakVisible={true}
                setInitialFocus={true}
                directionalHint={DirectionalHint.rightCenter}
                target={weatherAnchor}
                onDismiss={onDismiss}>
                <FocusTrapZone
                    isClickableOutsideFocusTrap={true}
                    forceFocusInsideTrap={false}
                    ref={component => (focusTrapZone.current = component)}>
                    <WeatherCard date={date} />
                </FocusTrapZone>
            </Callout>
        );
    };
    const showCallout = (event: React.MouseEvent<HTMLButtonElement>) => {
        const datapoint = new PerformanceDatapoint('ShowWeatherPeek');
        event.stopPropagation();
        if (shouldShowCallout) {
            focusTrapZone.current.focus();
        } else {
            setShouldShowCallout(true);
        }
        requestAnimationFrame(() => {
            datapoint.end();
        });
    };
    const onDismiss = (event: React.MouseEvent<HTMLButtonElement>) => {
        setShouldShowCallout(false);
        updateCurrentLocationIndex(weatherStore, 0);
    };
    let { date, showInTopRight = false } = props;
    let isValid = weatherStore.isValid;
    let weatherEnabled: boolean =
        getUserConfiguration().UserOptions.WeatherEnabled != WeatherEnabledStatus.Disabled;

    if (!weatherEnabled || !isFeatureEnabled('cal-mf-calendarWeatherDetails')) {
        return null;
    }
    if (!isValid) {
        trace.warn('The weather data is invalid, ignoring current weather render.');
        return null;
    }
    if (!weatherStore.weatherLocations || weatherStore.weatherLocations.length == 0) {
        trace.warn('There are no weather locations available, ignoring current weather render.');
        return null;
    }
    return isToday(date)
        ? renderCurrentWeather(date, showInTopRight)
        : renderWeatherForecast(date, showInTopRight);
});
